import React from "react";
import { Plus, Sparkles, Truck } from "lucide-react";
import {
    useCart,
    DISCOUNT_THRESHOLD,
    FREE_SHIPPING_THRESHOLD,
} from "../context/CartContext";
import { PRODUCTS, VIAL_IMAGE } from "../data/products";

// Trigger window: only surface this card when the customer is this close
// (or closer) to the next reward.
const TRIGGER_WINDOW = 25;

// Recommended "top-up" essentials — cheapest catalog items that comfortably
// nudge a cart past either threshold. Priority order: cheapest first; we
// keep adding alternatives down the list until two are found that aren't
// already in the cart.
const RECOMMENDED_IDS = [
    "bac-water-10ml",   // $14.99
    "syringes",         // $9.99
    "bac-water-3ml",    // $9.99
    "dsip",             // $44.99
    "selank",           // $49.99
    "melanotan-1",      // $49.99
];

const findProduct = (id) => {
    // Match either a top-level product or one of its variants by id.
    for (const p of PRODUCTS) {
        if (p.id === id) return { product: p, variant: null };
        if (Array.isArray(p.variants)) {
            const v = p.variants.find((vv) => vv.id === id);
            if (v) return { product: p, variant: v };
        }
    }
    return null;
};

export const CartRecommendation = () => {
    const { subtotal, items, addItem, discountApplied } = useCart();

    if (items.length === 0 || discountApplied) return null;

    const remainingToShip = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
    const remainingToDiscount = Math.max(0, DISCOUNT_THRESHOLD - subtotal);

    // Pick the next reward the customer is closest to (and within the window).
    let goal = null;
    if (subtotal < FREE_SHIPPING_THRESHOLD && remainingToShip <= TRIGGER_WINDOW) {
        goal = {
            kind: "shipping",
            remaining: remainingToShip,
            label: "FREE Shipping",
            message: `You're only $${remainingToShip.toFixed(2)} away from FREE Shipping!`,
            Icon: Truck,
        };
    } else if (subtotal >= FREE_SHIPPING_THRESHOLD && remainingToDiscount <= TRIGGER_WINDOW) {
        goal = {
            kind: "discount",
            remaining: remainingToDiscount,
            label: "10% OFF + FREE Shipping",
            message: `You're only $${remainingToDiscount.toFixed(2)} away from unlocking 10% OFF + FREE Shipping!`,
            Icon: Sparkles,
        };
    }

    if (!goal) return null;

    // Resolve recommended items, skipping any already in the cart.
    const inCartIds = new Set(items.map((i) => i.id));
    const recs = RECOMMENDED_IDS.map(findProduct)
        .filter(Boolean)
        .filter(({ product, variant }) => !inCartIds.has(variant ? variant.id : product.id))
        .slice(0, 2);

    if (recs.length === 0) return null;

    const handleAdd = ({ product, variant }) => {
        const v = variant || product;
        addItem({
            id: v.id,
            name: product.name,
            category: product.category,
            dosage: v.dosage,
            price: v.price,
            regular_price: v.regular_price,
            short: product.short,
            image: v.image || product.image,
        });
    };

    return (
        <div
            data-testid="cart-recommendation"
            className="relative overflow-hidden rounded-2xl border border-indigo-400/30 bg-gradient-to-br from-[#13093a]/85 via-[#15124a]/85 to-[#0b1448]/85 p-4"
        >
            <span className="pointer-events-none absolute -top-12 -right-10 w-44 h-44 rounded-full bg-violet-500/15 blur-3xl" />
            <span className="pointer-events-none absolute -bottom-14 -left-10 w-44 h-44 rounded-full bg-indigo-500/15 blur-3xl" />

            <div className="relative">
                <div className="flex items-start gap-2.5 mb-2">
                    <span className="inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-full bg-white/8 border border-white/15 shadow-[0_0_14px_rgba(167,139,250,0.35)]">
                        <goal.Icon className="w-3.5 h-3.5 text-violet-200" strokeWidth={2} />
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] tracking-[0.22em] uppercase text-violet-200/90 leading-none mb-1">
                            Complete Your Order &amp; Save More
                        </p>
                        <p className="text-[12px] text-white leading-snug">{goal.message}</p>
                    </div>
                </div>

                <div className="mt-3 space-y-2">
                    {recs.map(({ product, variant }) => {
                        const id = variant ? variant.id : product.id;
                        const dosage = variant ? variant.dosage : product.dosage;
                        const price = variant ? variant.price : product.price;
                        const image = variant ? variant.image : product.image;
                        return (
                            <div
                                key={id}
                                data-testid={`cart-rec-item-${id}`}
                                className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0A0A12]/55 px-3 py-2.5"
                            >
                                <div className="w-9 h-9 rounded-md bg-[#05050A] shrink-0 overflow-hidden flex items-center justify-center">
                                    <img
                                        src={image || VIAL_IMAGE}
                                        alt=""
                                        className="w-full h-full object-contain p-0.5"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[13px] text-white leading-tight truncate">{product.name}</p>
                                    <p className="text-[10px] text-slate-400 leading-tight">{dosage}</p>
                                </div>
                                <p className="text-[12px] text-slate-200 tabular-nums shrink-0">${Number(price).toFixed(2)}</p>
                                <button
                                    type="button"
                                    onClick={() => handleAdd({ product, variant })}
                                    data-testid={`cart-rec-add-${id}`}
                                    aria-label={`Add ${product.name} ${dosage} to cart`}
                                    className="
                                        shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full
                                        bg-indigo-500/85 hover:bg-indigo-400
                                        border border-indigo-300/40
                                        shadow-[0_0_14px_rgba(99,102,241,0.55)]
                                        transition-all duration-200
                                        active:scale-95
                                    "
                                >
                                    <Plus className="w-3.5 h-3.5 text-white" strokeWidth={2.4} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default CartRecommendation;
